import React, { useMemo } from "react";
import { AWARDS } from "../data/content";
import useInView from "../hooks/useInView";

export default function AwardsTicker() {
  const [ref, inView] = useInView();
  // Render enough copies so the marquee track is always wider than 2× the
  // viewport on wide screens. Even count keeps translateX(-50%) on item boundary.
  const copies = AWARDS.length >= 10 ? 2 : 4;

  // AWARDS is a module-level constant; copies derives from its length. Memo
  // with an empty dep array — the rendered marquee items never change after
  // mount, so we avoid rebuilding the JSX tree on parent re-renders.
  const tickerItems = useMemo(
    () =>
      Array.from({ length: copies }).flatMap((_, copyIdx) =>
        AWARDS.map((a, i) => (
          <div
            key={`${copyIdx}-${i}`}
            data-testid={`award-item-${copyIdx}-${i}`}
            className="flex items-center gap-5 whitespace-nowrap"
          >
            <div>
              {/* Line 1 — Award name + year. Smaller than single-line tickers
                  on purpose: two-line treatment + denser content needs a
                  slightly tighter type scale to stay readable. */}
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "clamp(1.125rem, 1.8vw, 1.375rem)",
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                  color: "var(--color-primary)",
                  lineHeight: 1.2,
                }}
              >
                {a.name},{" "}
                <span style={{ color: "var(--color-tertiary)" }}>{a.year}</span>
              </div>
              {/* Line 2 — Awarding organisation. */}
              <div
                className="label-sm"
                style={{
                  color: "var(--color-on-surface-variant)",
                  marginTop: 4,
                  textTransform: "none",
                  letterSpacing: "0.04em",
                  fontWeight: 500,
                }}
              >
                {a.org}
              </div>
            </div>
            {/* Red bullet dot — same separator pattern as the other tickers. */}
            <span
              aria-hidden
              style={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                background: "var(--color-tertiary)",
                display: "inline-block",
                flexShrink: 0,
              }}
            />
          </div>
        ))
      ),
    [copies]
  );

  return (
    <div
      ref={ref}
      data-testid="awards-ticker"
      className="ticker-fullbleed"
      style={{
        paddingTop: "var(--space-32)",
        paddingBottom: 8,
        overflow: "hidden",
      }}
    >
      {/* Transparent + red-dot bullets + same display font as the other tickers.
          Kept two-line treatment to preserve award + awarding organisation. */}
      <div
        className="marquee-track flex items-center"
        style={{
          width: "max-content",
          gap: 40,
          animationDuration: `${AWARDS.length * 6}s`,
          animationPlayState: inView ? "running" : "paused",
        }}
      >
        {tickerItems}
      </div>
    </div>
  );
}
